import type { Operator } from '../../../types/QueryBuilder';

import type { FieldType } from './fieldType';

/**
 * Display labels for each operator.
 * Typed as `Record<Operator, string>` so TypeScript will error if a new
 * operator is added to the `Operator` union without a corresponding entry here.
 */
export const OPERATOR_DISPLAY_MAP = {
  '=': 'is',
  '!=': 'is not',
  '>': 'is greater than',
  '>=': 'is greater than or equal to',
  '<': 'is less than',
  '<=': 'is less than or equal to',
  '^=': 'starts with',
  '=^': 'ends with',
  '**': 'contains',
  '!**': 'does not contain',
  '//': 'matches',
  '!//': 'does not match',
} as const satisfies Record<Operator, string>;

/** Flat options array derived from OPERATOR_DISPLAY_MAP, ready to pass to AutocompletePopover. */
export const OPERATOR_OPTIONS = (Object.entries(OPERATOR_DISPLAY_MAP) as Array<[Operator, string]>).map(
  ([value, displayName]) => ({ value, displayName }),
);

/**
 * Rendered operator label. Appends " one of" when `oneOf` is set (a single-value field with more
 * than one chosen value, e.g. "is one of"). Display only — the token/backend operator is unchanged,
 * so OPERATOR_DISPLAY_MAP / OPERATOR_OPTIONS (the operator picker) keep their base labels.
 */
export function getOperatorDisplay(operator: Operator, opts?: { oneOf?: boolean }): string {
  return opts?.oneOf ? `${OPERATOR_DISPLAY_MAP[operator]} one of` : OPERATOR_DISPLAY_MAP[operator];
}

/**
 * Operators that only ever carry a single value. The numeric comparisons (`>`, `>=`, `<`, `<=`)
 * are meaningless against a set — "greater than 5 AND 10" doesn't render as a comma-separated
 * multiselect, and the metrics backend lists them in singleValueOperators / SINGLE_VALUE_OPS and
 * 400s when given multiple values. The value picker renders single-select for these, and the
 * operator label never gets the "… one of" suffix (e.g. no "is less than or equal to one of").
 */
export const SINGLE_VALUE_OPERATORS = new Set<Operator>(['>', '>=', '<', '<=']);

/** Operators that make semantic sense for each field type. */
export const COMPATIBLE_OPERATORS: Record<FieldType, Operator[]> = {
  BOOLEAN: ['=', '!='],
  STRING: ['=', '!=', '**', '^=', '=^', '//', '!//', '>', '>=', '<', '<='],
};

/**
 * Operators that can semantically pair with the `__present__` value sentinel
 * ("match any value, just require the field to be set"). Used by:
 *   - LozengeFilter, to decide whether to surface the "present" autocomplete
 *     option in the value cell.
 *   - useQueryBuilderState's UPDATE_FILTER_OP reducer, to drop the sentinel
 *     when the user switches to an op that doesn't accept it.
 *
 * Keep this list and the consumer behavior in sync — `=` / `!=` round-trip
 * cleanly to/from the present mode, and `**` (contains) treats "present"
 * as a synonym for "non-empty," which is acceptable for that op too.
 */
export const PRESENT_COMPATIBLE_OPERATORS = new Set<Operator>(['=', '!=', '**']);

export function isOperatorCompatible(op: Operator, type: FieldType): boolean {
  return COMPATIBLE_OPERATORS[type].includes(op);
}

/** The default (first) compatible operator for a given field type. */
export function getDefaultOperator(type: FieldType): Operator {
  return COMPATIBLE_OPERATORS[type][0];
}
